import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { ContactsService, ApiError, OpenAPI } from '@/api';
import type {
  Contact,
  ContactLoginStatusResponse,
  CreateContactRequest,
  LinkContactLoginResponse,
  ProvisionContactLoginResponse,
  ReissueContactLoginResponse,
  RevokeContactLoginResponse,
  UpdateContactRequest,
} from '@/types';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/macro';
import { toUtcMidnightTimestamp } from '@/utils/dateUtils';
import { relationshipKeys } from './useChildContactRelationships';
import type { PaginationState } from '@tanstack/react-table';
import { usePaginatedQuery, type PaginatedResponse } from './usePagination';
import { fetchAllPages } from './fetchAllPages';
import { apiErrorFromResponse } from './billing/fetchApiError';
import { extractBackendError, translateBackendError } from '@/lib/translateBackendError';
// Deep-imported so a `vi.mock('@/api')` cannot leave the enum object undefined.
import { contacts_UpdateContactStatusRequest } from '@/api/models/contacts_UpdateContactStatusRequest';

// Query keys for contacts
export const contactKeys = {
  all: ['contacts'] as const,
  lists: () => [...contactKeys.all, 'list'] as const,
  list: (filters: Record<string, string | number | boolean>) => [...contactKeys.lists(), { filters }] as const,
  listPaginated: (params: { limit: number; offset: number }) => [...contactKeys.lists(), 'paginated', params] as const,
  details: () => [...contactKeys.all, 'detail'] as const,
  detail: (id: string) => [...contactKeys.details(), id] as const,
  audit: (id: string) => [...contactKeys.all, 'audit', id] as const,
  counts: () => [...contactKeys.all, 'counts'] as const,
  // Parent login status (issue #1892): detail-only, org-scoped.
  loginStatuses: () => [...contactKeys.all, 'loginStatus'] as const,
  loginStatus: (orgId: string | null | undefined, id: string) =>
    [...contactKeys.loginStatuses(), orgId ?? 'no-org', id] as const,
};

/**
 * Response type for paginated contacts list.
 * Matches the backend ContactListResponse struct.
 */
interface ContactListResponse {
  contacts: Contact[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Options for fetching paginated contacts
 */
export interface ContactsPaginatedOptions {
  /** Filter by status (comma-separated for multiple, e.g., "ACTIVE,UPCOMING") */
  status?: string;
  /** Filter by label IDs (comma-separated) */
  labelIds?: string;
  /** Search term to filter by firstname, lastname, email, or contact_internal_id */
  search?: string;
  /** Sort by column (default: "lastname") */
  sortBy?: string;
  /** Sort order (default: "asc") */
  sortOrder?: 'asc' | 'desc';
  /** Filter to only show contacts who are parents */
  parentsOnly?: boolean;
  /** Filter to only show contacts linked to children with current/future contracts */
  withContracts?: boolean;
}

/**
 * Fetch paginated contacts from the API with filtering and sorting.
 * Uses direct fetch since the generated service doesn't match the updated API response shape.
 */
async function fetchContactsPaginated(
  limit: number,
  offset: number,
  options?: ContactsPaginatedOptions
): Promise<PaginatedResponse<Contact>> {
  const baseUrl = OpenAPI.BASE || '';

  // Build query parameters
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });

  // Add optional filters
  if (options?.status) {
    params.set('status', options.status);
  }
  if (options?.labelIds) {
    params.set('label_ids', options.labelIds);
  }
  if (options?.search) {
    params.set('search', options.search);
  }
  if (options?.sortBy) {
    params.set('sort_by', options.sortBy);
  }
  if (options?.sortOrder) {
    params.set('sort_order', options.sortOrder);
  }
  if (options?.parentsOnly !== undefined) {
    params.set('parents_only', String(options.parentsOnly));
  }
  if (options?.withContracts !== undefined) {
    params.set('with_contracts', String(options.withContracts));
  }

  const url = `${baseUrl}/api/v1/contacts?${params.toString()}`;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Add auth token if available
  if (typeof OpenAPI.TOKEN === 'string') {
    headers['Authorization'] = `Bearer ${OpenAPI.TOKEN}`;
  } else if (typeof OpenAPI.TOKEN === 'function') {
    const token = await OpenAPI.TOKEN({
      method: 'GET',
      url,
      headers,
      body: undefined,
      mediaType: undefined,
      path: {},
      cookies: {},
      query: { limit, offset, ...options },
    });
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const response = await fetch(url, {
    method: 'GET',
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    throw await apiErrorFromResponse(response, url, 'Failed to fetch contacts');
  }

  const data: ContactListResponse = await response.json();

  return {
    data: data.contacts,
    total: data.total,
    limit: data.limit,
    offset: data.offset,
  };
}

/**
 * Hook to fetch paginated contacts with server-side pagination, filtering, and sorting.
 *
 * @param initialPageSize - Initial page size (default: 50)
 * @param options - Filter and sort options
 * @returns Paginated query result with filter/sort-aware refetching
 */
/** `pagination` makes the page state controlled (see `usePaginatedQuery`). */
export function useContactsPaginated(
  initialPageSize = 50,
  options?: ContactsPaginatedOptions,
  pagination?: PaginationState
) {
  // Include options in the query key so changes trigger refetch
  const queryKeyWithOptions = (params: { limit: number; offset: number }) => [
    ...contactKeys.listPaginated(params),
    { status: options?.status, labelIds: options?.labelIds, search: options?.search, sortBy: options?.sortBy, sortOrder: options?.sortOrder, parentsOnly: options?.parentsOnly, withContracts: options?.withContracts }
  ];

  return usePaginatedQuery<Contact>({
    queryKey: queryKeyWithOptions,
    queryFn: (limit, offset) => fetchContactsPaginated(limit, offset, options),
    initialPageSize,
    pagination,
  });
}

// Hook to fetch ALL contacts. A single request returns one page only, so this
// pages through the list with fetchAllPages until the server returns a short page.
export function useAllContacts() {
  const { isSignedIn } = useAuth();
  const { isAuthReady, orgId } = useAuthContext();

  return useQuery({
    queryKey: [...contactKeys.lists(), 'all', { orgId: orgId ?? '' }],
    queryFn: () =>
      fetchAllPages(async (limit, offset) => (await fetchContactsPaginated(limit, offset)).data ?? []),
    enabled: isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on 401/403 errors
      if (error instanceof ApiError) {
        if (error.status === 401 || error.status === 403) {
          return false;
        }
      }
      return failureCount < 3;
    }
  });
}

// Hook to fetch all contacts
export function useContacts(limit = 50, offset = 0) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  
  return useQuery({
    queryKey: contactKeys.list({ limit, offset }),
    queryFn: async () => {
      const response = await ContactsService.getApiV1Contacts(limit, offset);
      return response.contacts || [];
    },
    enabled: isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on 401/403 errors
      if (error instanceof ApiError) {
        if (error.status === 401 || error.status === 403) {
          return false;
        }
      }
      return failureCount < 3;
    }
  });
}

// Hook to fetch a single contact
export function useContact(id: string) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  
  return useQuery({
    queryKey: contactKeys.detail(id),
    queryFn: () => ContactsService.getApiV1Contacts1(id),
    enabled: !!id && isSignedIn && isAuthReady,
    staleTime: 0, // Always considered stale to ensure fresh data
    gcTime: 0, // Don't cache to prevent stale data issues
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });
}

/**
 * List contacts for a searchable single-select dropdown.
 *
 * Drives the list off the main list endpoint's `search` param rather than the
 * dedicated `/contacts/search` endpoint: the latter rejects an empty `q`, so it
 * can't serve the initial (untyped) list. Using the list endpoint lets one
 * server-side query cover both the empty term (first page, server-sorted) and a
 * typed term (server-filtered) — so the caller never needs a separate capped
 * bulk fetch for the initial list. `keepPreviousData` keeps the current rows
 * visible (with the combobox's fetching spinner) while a new term resolves.
 */
export function useContactSearchList(search: string, limit = 20) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  const term = search.trim();

  return useQuery({
    queryKey: [...contactKeys.lists(), 'searchList', { search: term, limit }],
    queryFn: async () => {
      const response = await ContactsService.getApiV1Contacts(limit, 0, undefined, term || undefined);
      return response.contacts || [];
    },
    enabled: isSignedIn && isAuthReady,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

// Hook to search contacts
export function useSearchContacts(query: string, limit = 50, offset = 0) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  
  return useQuery({
    queryKey: [...contactKeys.lists(), 'search', { query, limit, offset }],
    queryFn: async () => {
      const response = await ContactsService.getApiV1ContactsSearch(query, limit, offset);
      return response.contacts || [];
    },
    enabled: !!query && isSignedIn && isAuthReady,
  });
}

// Hook to get contact counts
export function useContactCounts() {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  
  return useQuery({
    queryKey: contactKeys.counts(),
    queryFn: () => ContactsService.getApiV1ContactsCounts(),
    enabled: isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Hook to create a contact
export function useCreateContact() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (contact: CreateContactRequest) => ContactsService.postApiV1Contacts(contact),
    onSuccess: (data) => {
      // Optimistically insert the new contact into cached Contact[] list queries
      // (useContacts, useAllContacts, useSearchContacts) so consumers like the
      // Add-Contact-Relationship dropdown can resolve the new id synchronously,
      // before the invalidation-triggered refetch lands. Queries with a
      // different cached shape (e.g. useContactsPaginated's PaginatedResponse)
      // are skipped here and reconciled by invalidateQueries below.
      if (data) {
        queryClient.setQueriesData(
          { queryKey: contactKeys.lists() },
          (old: unknown) => (Array.isArray(old) ? [...old, data] : old)
        );
      }
      queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contactKeys.counts() });
      // Invalidate detail cache so it fetches fresh with labels
      if (data?.id) {
        queryClient.invalidateQueries({ queryKey: contactKeys.detail(data.id) });
      }
      toast.success(i18n._('common.success.created'));
    },
    onError: (error: Error) => {
      // Handle specific error types
      const errorMessage = error.message || "Unknown error";
      const lowerMessage = errorMessage.toLowerCase();
      
      // Check for unique email constraint
      if (lowerMessage.includes("duplicate key") || 
          lowerMessage.includes("unique constraint") ||
          lowerMessage.includes("email already exists") ||
          lowerMessage.includes("already exists")) {
        toast.error(i18n._('contacts.toast.createDuplicateEmail'));
        return;
      }
      
      // Check for validation errors
      if (lowerMessage.includes("validation") || lowerMessage.includes("invalid")) {
        toast.error(i18n._('common.error.validationFailed'));
        return;
      }
      
      // Generic error fallback
      toast.error(i18n._('common.error.createFailed'));
    },
  });
}

// Hook to update a contact
export function useUpdateContact() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateContactRequest }) =>
      ContactsService.patchApiV1Contacts(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contactKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: contactKeys.counts() });
      // Invalidate entity-labels cache for this contact
      queryClient.invalidateQueries({ queryKey: ['entity-labels', 'contacts', variables.id] });
      // Invalidate audit queries to ensure fresh audit data after contact update
      queryClient.invalidateQueries({ queryKey: contactKeys.audit(variables.id) });
      // Invalidate relationship queries to refresh contact names in relationship cards
      queryClient.invalidateQueries({ queryKey: relationshipKeys.byContact(variables.id) });
      toast.success(i18n._('common.success.updated'));
    },
    onError: (error: Error) => {
      // Check for HTTP 409 conflict (duplicate email)
      if (error instanceof ApiError && error.status === 409) {
        toast.error(i18n._('contacts.toast.createDuplicateEmail'));
        return;
      }
      
      // Handle specific error types
      const errorMessage = error.message || "Unknown error";
      const lowerMessage = errorMessage.toLowerCase();
      
      // Check for validation errors
      if (lowerMessage.includes("validation") || lowerMessage.includes("invalid")) {
        console.error('Failed to update contact (validation):', error);
        toast.error(i18n._(msg`Please check the form data and try again.`));
        return;
      }

      // Check for concurrent modification
      if (lowerMessage.includes("concurrent") || lowerMessage.includes("modified")) {
        console.error('Failed to update contact (concurrent modification):', error);
        toast.error(i18n._(msg`The contact record has been modified by another user. Please refresh and try again.`));
        return;
      }

      // Generic error fallback
      console.error('Failed to update contact:', error);
      toast.error(i18n._(msg`Failed to update contact. Please try again or contact support.`));
    },
  });
}

// Hook to delete a contact
export function useDeleteContact() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({ id }: { id: string }) => ContactsService.deleteApiV1Contacts(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contactKeys.counts() });
      // Invalidate audit queries to ensure fresh audit data after contact deletion
      queryClient.invalidateQueries({ queryKey: contactKeys.audit(variables.id) });
      toast.success(i18n._(msg`Contact deleted successfully`));
    },
    onError: (error: unknown) => {
      console.error('Failed to delete contact:', error);
      const { code } = extractBackendError(error);
      toast.error(translateBackendError(code, i18n, msg`Failed to delete contact`));
    },
  });
}

// Hook to update contact status
export function useUpdateContactStatus() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'ACTIVE' | 'INACTIVE' | 'UPCOMING' }) =>
      // The generated enum is nominal but value-identical to the literal union the
      // callers pass; indexing the enum object substitutes the member at no runtime
      // cost (member NAMES equal their values here).
      ContactsService.patchApiV1ContactsStatus(id, {
        status: contacts_UpdateContactStatusRequest.status[status],
      }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contactKeys.counts() });
      queryClient.setQueryData(contactKeys.detail(variables.id), data);
      // Invalidate relationship queries in case status affects display
      queryClient.invalidateQueries({ queryKey: relationshipKeys.byContact(variables.id) });
      toast.success(i18n._('contacts.toast.statusUpdateSuccess'));
    },
    onError: (error: unknown) => {
      console.error('Failed to update contact status:', error);
      const { code } = extractBackendError(error);
      toast.error(translateBackendError(code, i18n, msg`Failed to update contact status`));
    },
  });
}

// Hook to update contact customer score
export function useUpdateContactCustomerScore() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({ id, customerScore }: { id: string; customerScore: number }) =>
      ContactsService.patchApiV1ContactsCustomerScore(id, { customerScore }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contactKeys.detail(variables.id) });
      toast.success(i18n._(msg`Customer score updated successfully`));
    },
    onError: (error: unknown) => {
      console.error('Failed to update customer score:', error);
      const { code } = extractBackendError(error);
      toast.error(translateBackendError(code, i18n, msg`Failed to update customer score`));
    },
  });
}

// Hook to update a single field of a contact (for table editing)
export function useUpdateContactField<K extends keyof Contact>() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation<
    Contact,
    Error,
    { id: string; field: K; value: Contact[K] }
  >({
    mutationFn: async ({ 
      id, 
      field, 
      value 
    }) => {
      // No field mapping needed since it's 1:1
      const apiField = field;
      
      // Create update object with only the changed field
      const updateData: Partial<UpdateContactRequest> = {};
      
      // Handle special formatting for different field types
      if (apiField === 'birthdate' && value) {
        // Format date in local time as UTC timestamp at midnight to avoid timezone shifting
        // This matches the approach used in employee and child forms.
        //
        // Sent as the `{ time }` envelope the generated `shared_FlexibleDate` declares.
        // `FlexibleDate.UnmarshalJSON` tries that object form FIRST and parses the same
        // RFC3339 string out of it (internal/handlers/shared/common_types.go:126-155),
        // so the parsed date is identical to the bare-string form this used to send.
        updateData.birthdate = { time: toUtcMidnightTimestamp(value as Date | string) };
      } else if (apiField === 'nationalities' && value) {
        // Ensure nationalities is always an array of ISO codes. `value` is typed over
        // EVERY `Contact` key here (the `apiField` test narrows the key, not the
        // value), so the element type has to be checked rather than assumed.
        const nationalities: string[] = [];
        if (Array.isArray(value)) {
          for (const entry of value) {
            if (typeof entry === 'string') nationalities.push(entry);
          }
        } else if (typeof value === 'string') {
          nationalities.push(value);
        }
        updateData.nationalities = nationalities;
      } else {
        // A write through a union-typed key would have to satisfy the INTERSECTION of
        // every candidate field type (which is `never` here). `Object.assign` performs
        // the identical single-key write without that constraint.
        Object.assign(updateData, { [apiField]: value });
      }
      
      return ContactsService.patchApiV1Contacts(id, updateData);
    },
    onSuccess: (_, variables) => {
      // Invalidate contact list queries to refetch data
      queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
      // Invalidate the specific contact detail
      queryClient.invalidateQueries({ queryKey: contactKeys.detail(variables.id) });
      // Invalidate audit queries to ensure fresh audit data after field update
      queryClient.invalidateQueries({ queryKey: contactKeys.audit(variables.id) });
      // Also invalidate all relationship lists to ensure any view showing this contact's name is refreshed
      queryClient.invalidateQueries({ queryKey: relationshipKeys.lists() });
      toast.success(i18n._('contacts.toast.updateFieldSuccess'));
    },
    onError: (error: Error) => {
      const errorMessage = error.message || 'Failed to update contact';
      console.error('Contact update error:', errorMessage);
      // Let the table component handle the toast notification
    },
  });
}

// Hook to export contacts to CSV
export function useExportContactsCSV() {
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({
      status,
      ids
    }: {
      status?: 'ACTIVE' | 'INACTIVE' | 'UPCOMING';
      ids?: string[]
    }) => ContactsService.getApiV1ContactsExportCsv(status, ids),
    onSuccess: (blob) => {
      // Create a download link for the CSV file
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contacts_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(i18n._(msg`Contacts exported successfully`));
    },
    onError: (error: unknown) => {
      console.error('Failed to export contacts:', error);
      const { code } = extractBackendError(error);
      toast.error(translateBackendError(code, i18n, msg`Failed to export contacts`));
    },
  });
}

// Hook to get contact by internal ID
export function useContactByInternalId(internalId: string) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  
  return useQuery({
    queryKey: [...contactKeys.details(), 'internal', internalId],
    queryFn: () => ContactsService.getApiV1ContactsByInternalId(internalId),
    enabled: !!internalId && isSignedIn && isAuthReady,
  });
}

// Helper function to invalidate contact audit queries
export function invalidateContactAuditQueries(queryClient: ReturnType<typeof useQueryClient>, contactId: string) {
  queryClient.invalidateQueries({ queryKey: contactKeys.audit(contactId) });
}

// ---------------------------------------------------------------------------
// Parent login provisioning (issue #1892)
// ---------------------------------------------------------------------------

/**
 * Invalidate the contact detail, the lists (table badge) and the login status
 * after a login action, so the panel and header badge re-render with the new
 * clerkUserId. The one-time password is never written to the cache.
 */
export function invalidateContactLoginCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  contactId: string,
) {
  queryClient.invalidateQueries({ queryKey: contactKeys.detail(contactId) });
  queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
  queryClient.invalidateQueries({ queryKey: contactKeys.loginStatuses() });
}

/**
 * Detail-only login status: a best-effort "Signed in" flag from one Clerk
 * lookup. Enabled only when the caller opts in (admin with a linked login).
 */
export function useContactLoginStatus(id: string, enabled: boolean) {
  const { orgId } = useAuthContext();

  return useQuery<ContactLoginStatusResponse>({
    queryKey: contactKeys.loginStatus(orgId, id),
    queryFn: () => ContactsService.getApiV1ContactsLoginStatus(id),
    enabled: enabled && !!id,
    // The signed-in flag changes rarely; avoid hammering Clerk on every render.
    staleTime: 60_000,
  });
}

/**
 * Create a parent login. The one-time password comes back in the mutation
 * result only; no toast here (the panel shows the password or a translated error).
 */
export function useProvisionContactLogin() {
  const queryClient = useQueryClient();

  return useMutation<ProvisionContactLoginResponse, unknown, { id: string }>({
    mutationFn: ({ id }) => ContactsService.postApiV1ContactsProvisionLogin(id),
    onSuccess: (_data, variables) => {
      invalidateContactLoginCaches(queryClient, variables.id);
    },
  });
}

/** Link the Clerk user that already exists for the contact's main email. */
export function useLinkContactLogin() {
  const queryClient = useQueryClient();

  return useMutation<LinkContactLoginResponse, unknown, { id: string }>({
    mutationFn: ({ id }) => ContactsService.postApiV1ContactsLinkLogin(id),
    onSuccess: (_data, variables) => {
      invalidateContactLoginCaches(queryClient, variables.id);
    },
  });
}

/** Re-issue a one-time password for a linked parent (returned once, never cached). */
export function useReissueContactLogin() {
  const queryClient = useQueryClient();

  return useMutation<ReissueContactLoginResponse, unknown, { id: string }>({
    mutationFn: ({ id }) => ContactsService.postApiV1ContactsReissueLogin(id),
    onSuccess: (_data, variables) => {
      invalidateContactLoginCaches(queryClient, variables.id);
    },
  });
}

/** Revoke a parent login. The Clerk user is never deleted. */
export function useRevokeContactLogin() {
  const queryClient = useQueryClient();

  return useMutation<RevokeContactLoginResponse, unknown, { id: string }>({
    mutationFn: ({ id }) => ContactsService.postApiV1ContactsRevokeLogin(id),
    onSuccess: (_data, variables) => {
      invalidateContactLoginCaches(queryClient, variables.id);
    },
  });
}
